import { useEffect, useRef, type CSSProperties } from 'react';
import { clipEnd, type ImageLayer } from '@bannermatic/core';
import { boxShadow } from '../style';

export interface ImageLayerViewProps {
  layer: ImageLayer;
  mode: 'render' | 'editor';
  resolveSrc?: (src: string) => string;
  /** Length of the animation, s: set when clips play (animation preview, video render). */
  videoSeconds?: number;
  /** When the clip plays (its scene), ms; none: the whole video. */
  videoWindow?: [number, number];
  /** Editor preview: play the clip on its own, with its sound settings. */
  live?: boolean;
}

/** A clip playing by itself from its trim point, looping (or stopping) like in the video. */
function LiveClip({
  video,
  src,
  poster,
  style,
}: {
  video: NonNullable<ImageLayer['video']>;
  src: string;
  poster?: string;
  style: CSSProperties;
}) {
  const ref = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.muted = video.muted;
    el.volume = Math.max(0, Math.min(1, video.volume));
    el.playbackRate = video.speed || 1;
  }, [video.muted, video.volume, video.speed]);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.currentTime = video.trimStart / 1000;
    // Sound may be refused without a user gesture: fall back to a silent preview
    void el.play().catch(() => {
      el.muted = true;
      void el.play().catch(() => {});
    });
  }, [src, video.trimStart]);
  return (
    <video
      ref={ref}
      data-bm-live-video
      src={src}
      poster={poster}
      playsInline
      preload="auto"
      onTimeUpdate={(e) => {
        // The used part ends before the file does: loop back or stop there
        const el = e.currentTarget;
        if (el.currentTime * 1000 < clipEnd(video) - 30) return;
        if (video.loop) el.currentTime = video.trimStart / 1000;
        else el.pause();
      }}
      onEnded={(e) => {
        if (!video.loop) return;
        e.currentTarget.currentTime = video.trimStart / 1000;
        void e.currentTarget.play().catch(() => {});
      }}
      style={style}
    />
  );
}

export function ImageLayerView({
  layer,
  mode,
  resolveSrc,
  videoSeconds,
  videoWindow,
  live,
}: ImageLayerViewProps) {
  const filters: string[] = [];
  if (layer.grayscale) filters.push('grayscale(1)');
  if (layer.blur > 0) filters.push(`blur(${layer.blur}px)`);
  if (layer.brightness !== 1) filters.push(`brightness(${layer.brightness})`);

  const frame: CSSProperties = {
    width: '100%',
    height: '100%',
    overflow: 'hidden',
    borderRadius: layer.borderRadius || undefined,
    border: layer.borderWidth > 0 ? `${layer.borderWidth}px solid ${layer.borderColor}` : undefined,
    boxSizing: 'border-box',
    boxShadow: boxShadow(layer.shadow),
  };

  // A clip without a poster frame (e.g. set through the API) still plays in videos
  const clipOnly = Boolean(layer.video?.src) && (live || videoSeconds !== undefined);
  if (!layer.src && !clipOnly) {
    if (mode === 'render') return <div style={frame} />;
    return (
      <div
        style={{
          ...frame,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'repeating-conic-gradient(#e5e7eb 0% 25%, #f3f4f6 0% 50%) 50% / 24px 24px',
          color: '#6b7280',
        }}
      >
        <svg
          width="30%"
          height="30%"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          style={{ maxWidth: 96, maxHeight: 96 }}
        >
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <circle cx="9" cy="9" r="2" />
          <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />
        </svg>
      </div>
    );
  }

  const flip =
    layer.flipX || layer.flipY
      ? `scale(${layer.flipX ? -1 : 1}, ${layer.flipY ? -1 : 1})`
      : undefined;

  const media: CSSProperties = {
    display: 'block',
    width: '100%',
    height: '100%',
    objectFit: layer.fit,
    objectPosition: `${layer.focusX * 100}% ${layer.focusY * 100}%`,
    filter: filters.length ? filters.join(' ') : undefined,
    transform: flip,
    userSelect: 'none',
    pointerEvents: 'none',
  };
  const resolve = (src: string) => (resolveSrc ? resolveSrc(src) : src);

  if (layer.video && live && videoSeconds === undefined) {
    return (
      <div style={frame}>
        <LiveClip
          video={layer.video}
          src={resolve(layer.video.src)}
          poster={layer.src ? resolve(layer.src) : undefined}
          style={media}
        />
      </div>
    );
  }

  if (layer.video && videoSeconds !== undefined) {
    const v = layer.video;
    // data-* timing is read by the video capture engine, which draws the clip frame by frame
    return (
      <div style={frame}>
        <video
          id={`bmv-${layer.id}`}
          data-bm-video={layer.id}
          src={resolve(v.src)}
          poster={layer.src ? resolve(layer.src) : undefined}
          muted={v.muted}
          loop={v.loop}
          playsInline
          preload="auto"
          data-start={String((videoWindow?.[0] ?? 0) / 1000)}
          data-duration={String(
            videoWindow ? (videoWindow[1] - videoWindow[0]) / 1000 : videoSeconds,
          )}
          data-media-start={String(v.trimStart / 1000)}
          data-playback-rate={v.speed && v.speed !== 1 ? String(v.speed) : undefined}
          data-has-audio={v.muted ? undefined : 'true'}
          data-volume={v.muted ? undefined : String(v.volume)}
          style={media}
        />
      </div>
    );
  }

  return (
    <div style={frame}>
      <img
        src={resolveSrc ? resolveSrc(layer.src) : layer.src}
        alt=""
        draggable={false}
        decoding="sync"
        style={{
          display: 'block',
          width: '100%',
          height: '100%',
          objectFit: layer.fit,
          objectPosition: `${layer.focusX * 100}% ${layer.focusY * 100}%`,
          filter: filters.length ? filters.join(' ') : undefined,
          transform: flip,
          userSelect: 'none',
          pointerEvents: 'none',
        }}
      />
    </div>
  );
}
